import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Boxes,
  Receipt,
  Settings as SettingsIcon,
  LogOut,
  Sun,
  Moon,
  CreditCard,
  ListChecks,
  ShoppingBag,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";

const ADMIN_NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, testid: "nav-admin-dashboard", end: true },
  { to: "/admin/users", label: "Users", icon: Users, testid: "nav-admin-users" },
  { to: "/admin/plans", label: "Plans", icon: ListChecks, testid: "nav-admin-plans" },
  { to: "/admin/shared-accounts", label: "Shared Accounts", icon: Boxes, testid: "nav-admin-shared" },
  { to: "/admin/subscriptions", label: "Subscriptions", icon: Receipt, testid: "nav-admin-subs" },
  { to: "/admin/payments", label: "Payments", icon: CreditCard, testid: "nav-admin-payments" },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon, testid: "nav-admin-settings" },
];

const USER_NAV = [
  { to: "/account", label: "My Subscriptions", icon: LayoutDashboard, testid: "nav-user-dashboard", end: true },
  { to: "/account/plans", label: "Buy Plan", icon: ShoppingBag, testid: "nav-user-plans" },
  { to: "/account/payments", label: "My Payments", icon: CreditCard, testid: "nav-user-payments" },
];

export default function Layout({ children }) {
  const { user, logout, isAdmin } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const NAV = isAdmin ? ADMIN_NAV : USER_NAV;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      <aside
        data-testid="sidebar"
        className="hidden md:flex md:flex-col w-64 border-r border-border bg-card/40 backdrop-blur-xl p-5 gap-2"
      >
        <div className="mb-6">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-foreground text-background flex items-center justify-center font-heading font-black">
              S
            </div>
            <div>
              <h1 className="font-heading font-black text-lg leading-none">premiumtools.store</h1>
              <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-0.5">
                {isAdmin ? "Admin Console" : "Member Area"}
              </p>
            </div>
          </div>
        </div>
        <nav className="flex flex-col gap-1">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
            >
              <item.icon className="w-4 h-4" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto pt-6 border-t border-border">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center font-mono text-xs font-bold">
              {(user?.name || user?.email || "U")[0].toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold truncate">{user?.name || "User"}</p>
              <p className="text-[10px] text-muted-foreground truncate font-mono">{user?.email}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full justify-start mt-1"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-border bg-background/60 backdrop-blur-xl flex items-center px-6 gap-3 sticky top-0 z-30">
          <div className="md:hidden font-heading font-black text-lg">premiumtools.store</div>
          <div className="flex-1" />
        </header>
        <main className="flex-1 overflow-x-hidden">
          <div className="max-w-7xl mx-auto p-6 md:p-8">{children}</div>
        </main>
      </div>
    </div>
  );
}
